import { ACTIVE_VIDEO_NOTIFICATIONS_QUERY_KEY } from '../constants';

/**
 * Timing and naming for the cross-tab recall of the agent's notification queue.
 * Every tunable lives here, so the cadence can be changed in one place.
 */

/**
 * Bumped whenever a stored record or a broadcast message changes shape. Older
 * records and messages are dropped on read rather than half-understood.
 */
export const VIDEO_SYNC_VERSION = 1;

/**
 * Periodic recall window. The delay is redrawn from this range on every tick
 * rather than fixed, so tabs opened at the same moment drift apart instead of
 * hitting the endpoint in lockstep forever.
 */
export const RECALL_MIN_MS = 10 * 60 * 1000;
export const RECALL_MAX_MS = 20 * 60 * 1000;

/**
 * Minimum gap between two network recalls — measured across *all* tabs of this
 * app, because the last-run stamp lives in shared storage. This is what makes
 * "landing on a tab refetches, unless someone already did it in the last ten
 * minutes" true for the browser as a whole rather than per tab.
 */
export const RECALL_MIN_GAP_MS = 10 * 60 * 1000;

/** How long one tab may hold the right to fetch before others may take over. */
export const RECALL_LEASE_MS = 15 * 1000;

/** After a failed recall, no tab retries for this long. */
export const RECALL_RETRY_GAP_MS = 60 * 1000;

/** Cap on the consecutive-failure backoff multiplier for the recall timer. */
export const RECALL_MAX_BACKOFF_FACTOR = 4;

/**
 * `focus` fires when moving between two already-visible windows, where
 * `visibilitychange` does not — but it also fires on every click back into the
 * page, so it needs a short throttle of its own.
 */
export const FOCUS_THROTTLE_MS = 1000;

/** Watched ids kept per identity; oldest are dropped past the cap. */
export const WATCHED_IDS_MAX = 100;
export const WATCHED_IDS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** b2c and Backoffice are separate origins; the segment is for legibility. */
const APP_SEGMENT = process.env.REACT_APP_TYPE === 'bo' ? 'bo' : 'b2c';

/**
 * `mwv:v1:{app}:{identity}:{suffix}`.
 *
 * The identity segment is what keeps two people sharing a workstation apart:
 * nothing written for `agent:42` is ever read back for `agent:43`.
 */
export const buildSyncKey = (scope: string, suffix: string) =>
  `mwv:v${VIDEO_SYNC_VERSION}:${APP_SEGMENT}:${scope}:${suffix}`;

export const buildChannelName = (scope: string) =>
  `mwv:v${VIDEO_SYNC_VERSION}:${APP_SEGMENT}:${scope}`;

/**
 * Query key for the pending queue, namespaced by identity so a user switch in
 * the same browser can never render the previous user's videos.
 */
export const activeVideoNotificationsQueryKey = (scope?: string | null) => [
  ACTIVE_VIDEO_NOTIFICATIONS_QUERY_KEY,
  scope ?? 'anon',
];
